/**
 * Offline / legacy fallback for listing market taxonomy + SEO discovery tags.
 * Live data: GET seller/listings/:designId/wizard → snapshot.taxonomy (see useListingMarketTaxonomy).
 */

import type {
  IndexableLandingPageRuleDto,
  TaxonomyOptionDto,
} from "../../../../types/listing-wizard.types";

export type NicheOption = { id: string; label: string };
export type SubNicheOption = { id: string; label: string };

export const LISTING_NICHES: NicheOption[] = [
  { id: "lifestyle", label: "Lifestyle & everyday" },
  { id: "sports-fitness", label: "Sports & fitness" },
  { id: "music-entertainment", label: "Music & entertainment" },
  { id: "holidays-celebrations", label: "Holidays & celebrations" },
  { id: "humor-memes", label: "Humor & memes" },
  { id: "arts-culture", label: "Arts & culture" },
  { id: "causes-awareness", label: "Causes & awareness" },
  { id: "gaming-esports", label: "Gaming & esports" },
  { id: "travel-adventure", label: "Travel & adventure" },
  { id: "food-drink", label: "Food & drink" },
];

export const LISTING_SUB_NICHES: Record<string, SubNicheOption[]> = {
  lifestyle: [
    { id: "minimal-everyday", label: "Minimal / everyday" },
    { id: "streetwear", label: "Streetwear" },
    { id: "work-office", label: "Work & office" },
    { id: "cozy-loungewear", label: "Cozy & loungewear" },
    { id: "couples-matching", label: "Couples / matching" },
    { id: "family-kids", label: "Family & kids" },
  ],
  "sports-fitness": [
    { id: "football", label: "Football / soccer" },
    { id: "basketball", label: "Basketball" },
    { id: "running-gym", label: "Running & gym" },
    { id: "cycling", label: "Cycling" },
    { id: "martial-arts", label: "Martial arts" },
    { id: "winter-sports", label: "Winter sports" },
  ],
  "music-entertainment": [
    { id: "band-artist", label: "Band / artist" },
    { id: "hip-hop", label: "Hip-hop" },
    { id: "rock-alternative", label: "Rock & alternative" },
    { id: "electronic", label: "Electronic / DJ" },
    { id: "k-pop-anime", label: "K-pop & anime style" },
    { id: "festivals", label: "Festivals & concerts" },
  ],
  "holidays-celebrations": [
    { id: "birthday", label: "Birthday" },
    { id: "graduation", label: "Graduation" },
    { id: "wedding-bachelor", label: "Wedding / bachelor(ette)" },
    { id: "ramadan-eid", label: "Ramadan / Eid" },
    { id: "christmas-winter", label: "Christmas / winter" },
    { id: "valentines", label: "Valentine's" },
    { id: "back-to-school", label: "Back to school" },
  ],
  "humor-memes": [
    { id: "memes-internet", label: "Memes & internet" },
    { id: "sarcastic-quotes", label: "Sarcastic quotes" },
    { id: "pun-typography", label: "Puns & typography" },
    { id: "office-humor", label: "Office humor" },
  ],
  "arts-culture": [
    { id: "abstract-geometric", label: "Abstract & geometric" },
    { id: "illustration", label: "Illustration" },
    { id: "photography-style", label: "Photo / collage style" },
    { id: "vintage-retro", label: "Vintage & retro" },
    { id: "fine-art", label: "Fine art inspired" },
  ],
  "causes-awareness": [
    { id: "environment", label: "Environment" },
    { id: "mental-health", label: "Mental health" },
    { id: "equality-rights", label: "Equality & rights" },
    { id: "local-community", label: "Local / community" },
  ],
  "gaming-esports": [
    { id: "esports", label: "Esports" },
    { id: "retro-gaming", label: "Retro gaming" },
    { id: "rpgs-streamers", label: "RPGs & streamers" },
    { id: "fps-competitive", label: "FPS / competitive" },
  ],
  "travel-adventure": [
    { id: "mountains-outdoors", label: "Mountains & outdoors" },
    { id: "beach-tropical", label: "Beach & tropical" },
    { id: "city-skyline", label: "City & skyline" },
    { id: "road-trip", label: "Road trip & van life" },
  ],
  "food-drink": [
    { id: "coffee-tea", label: "Coffee & tea" },
    { id: "bbq-street-food", label: "BBQ & street food" },
    { id: "sweet-treats", label: "Sweet treats" },
    { id: "healthy-organic", label: "Healthy / organic" },
  ],
};

export function getSubNichesForNiche(nicheId: string): SubNicheOption[] {
  return LISTING_SUB_NICHES[nicheId] ?? [];
}

/** Lowercase slug for search / design.tags (never free-typed). */
export function toTagSlug(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/['']/g, "")
    .replace(/\s*&\s*/g, "-")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Curated shop SEO tags sellers can pick from (union with smart suggestions).
 * Keep hyphenated; shop filters match exactly or substring on design.tags.
 */
export const SEO_TAG_CATALOG: string[] = [
  "morocco",
  "custom-tee",
  "custom-hoodie",
  "personalized",
  "gift",
  "birthday-gift",
  "minimalist",
  "bold",
  "retro",
  "streetwear",
  "anime",
  "meme",
  "typography",
  "vintage",
  "abstract",
  "music",
  "sports",
  "football",
  "fitness",
  "gaming",
  "travel",
  "coffee",
  "funny",
  "quote",
  "couples",
  "family",
  "kids",
  "unisex",
  "men",
  "women",
  "oversized",
  "graphic-tee",
  "statement",
  "festival",
  "summer",
  "winter",
  "eid",
  "ramadan",
  "graduation",
  "valentine",
  "eco-friendly",
  "awareness",
  "apparel",
  "accessories",
  "drinkware",
  "t-shirt",
  "hoodie",
  "sweatshirt",
  "tank-top",
  "long-sleeve-tee",
  "tote-bag",
  "mug",
  "teens",
  "families",
  "friends",
  "gamers",
  "artists",
  "everyday",
  "k-pop",
];

/** Full allow-list for Step 6 chips = catalog + every niche/sub-niche slug (single source for UI). */
export function buildListingDiscoveryTagCatalog(): string[] {
  const set = new Set<string>(SEO_TAG_CATALOG);
  for (const n of LISTING_NICHES) {
    set.add(n.id);
  }
  for (const subs of Object.values(LISTING_SUB_NICHES)) {
    for (const s of subs) {
      set.add(s.id);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Mirrors server `listing-market-taxonomy` MVP set — offline only. */
export const FALLBACK_PRODUCT_CATEGORIES: TaxonomyOptionDto[] = [
  {
    key: "apparel",
    label: "Apparel",
    description: "Wearables: tees, hoodies, and similar garments.",
    parentKey: null,
    isActive: true,
    sortOrder: 10,
  },
  {
    key: "accessories",
    label: "Accessories",
    description: "Bags and add-on items.",
    parentKey: null,
    isActive: true,
    sortOrder: 20,
  },
  {
    key: "drinkware",
    label: "Drinkware",
    description: "Mugs and drink-related blanks.",
    parentKey: null,
    isActive: true,
    sortOrder: 30,
  },
];

export const FALLBACK_PRODUCT_TYPES: TaxonomyOptionDto[] = [
  {
    key: "t-shirt",
    label: "T-Shirt",
    parentKey: "apparel",
    isActive: true,
    sortOrder: 10,
    aliases: ["tee", "tshirt"],
  },
  {
    key: "hoodie",
    label: "Hoodie",
    parentKey: "apparel",
    isActive: true,
    sortOrder: 20,
  },
  {
    key: "sweatshirt",
    label: "Sweatshirt",
    parentKey: "apparel",
    isActive: true,
    sortOrder: 30,
  },
  {
    key: "tank-top",
    label: "Tank top",
    parentKey: "apparel",
    isActive: true,
    sortOrder: 40,
  },
  {
    key: "long-sleeve-tee",
    label: "Long sleeve tee",
    parentKey: "apparel",
    isActive: true,
    sortOrder: 50,
  },
  {
    key: "tote-bag",
    label: "Tote bag",
    parentKey: "accessories",
    isActive: true,
    sortOrder: 10,
  },
  {
    key: "mug",
    label: "Mug",
    parentKey: "drinkware",
    isActive: true,
    sortOrder: 10,
  },
];

const opt = (
  key: string,
  label: string,
  sortOrder: number,
  description?: string
): TaxonomyOptionDto => ({
  key,
  label,
  description,
  parentKey: null,
  isActive: true,
  sortOrder,
});

export const FALLBACK_AUDIENCE_OPTIONS: TaxonomyOptionDto[] = [
  opt("men", "Men", 10),
  opt("women", "Women", 20),
  opt("unisex", "Unisex", 30),
  opt("kids", "Kids", 40),
  opt("teens", "Teens", 50),
  opt("couples", "Couples", 60),
  opt("families", "Families", 70),
  opt("friends", "Friends", 80),
  opt("gamers", "Gamers", 90),
  opt("artists", "Artists", 100),
];

export const FALLBACK_OCCASION_OPTIONS: TaxonomyOptionDto[] = [
  opt("birthday", "Birthday", 10),
  opt("graduation", "Graduation", 20),
  opt("ramadan-eid", "Ramadan / Eid", 30),
  opt("valentines", "Valentine's", 40),
  opt("back-to-school", "Back to school", 50),
  opt("everyday", "Everyday", 60),
  opt("gift", "Gift", 70),
];

export const FALLBACK_STYLE_OPTIONS: TaxonomyOptionDto[] = [
  opt("minimalist", "Minimalist", 10),
  opt("bold", "Bold", 20),
  opt("retro", "Retro", 30),
  opt("streetwear", "Streetwear", 40),
  opt("anime", "Anime", 50),
  opt("football", "Football", 60),
  opt("k-pop", "K-pop", 70),
  opt("meme", "Meme", 80),
  opt("typography", "Typography", 90),
];

export const FALLBACK_INDEXABLE_RULES: IndexableLandingPageRuleDto[] = [
  {
    key: "product-detail-published",
    label: "Published PDP",
    description: "Single product listing pages when public and valid.",
    indexable: true,
  },
  {
    key: "faceted-multi-filter",
    label: "Multi-facet URLs",
    description: "Shop URLs with many combined filters should be noindex with a canonical parent.",
    indexable: false,
  },
];

const AUDIENCE_LABEL_MAP = new Map(
  FALLBACK_AUDIENCE_OPTIONS.map((o) => [o.label.toLowerCase(), o.key])
);
const OCCASION_LABEL_MAP = new Map(
  FALLBACK_OCCASION_OPTIONS.map((o) => [o.label.toLowerCase(), o.key])
);
const STYLE_LABEL_MAP = new Map(
  FALLBACK_STYLE_OPTIONS.map((o) => [o.label.toLowerCase(), o.key])
);

export function buyerIntentLabelToKey(
  label: string,
  kind: "audience" | "occasion" | "style"
): string | null {
  const n = label.trim().toLowerCase();
  const map =
    kind === "audience"
      ? AUDIENCE_LABEL_MAP
      : kind === "occasion"
        ? OCCASION_LABEL_MAP
        : STYLE_LABEL_MAP;
  if (map.has(n)) {
    const v = map.get(n);
    if (v !== undefined) return v;
  }
  if (kind === "occasion") {
    if (n === "ramadan/eid" || n === "ramadan eid") return "ramadan-eid";
  }
  const slug = toTagSlug(label);
  const list =
    kind === "audience"
      ? FALLBACK_AUDIENCE_OPTIONS
      : kind === "occasion"
        ? FALLBACK_OCCASION_OPTIONS
        : FALLBACK_STYLE_OPTIONS;
  const byKey = list.find((o) => o.key === slug);
  return byKey ? byKey.key : null;
}

export function deriveIntentKeysFromLegacyLabels(
  labels: string[] | undefined,
  kind: "audience" | "occasion" | "style"
): string[] {
  if (!Array.isArray(labels)) return [];
  const out: string[] = [];
  for (const lab of labels) {
    const k = buyerIntentLabelToKey(String(lab), kind);
    if (k && !out.includes(k)) out.push(k);
  }
  return out;
}
